import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicLayout from "@/components/PublicLayout";

const TIERS = [
  {
    name: "Guest", price_m: "Free", price_y: null, plan_code_m: null,
    features: ["Browse public articles", "Listen podcast intros", "View company snippets"],
    cta: "Sign up free", testid: "tier-guest",
  },
  {
    name: "Member", price_m: 99, price_y: 990, plan_code_m: "member_monthly", plan_code_y: "member_yearly",
    features: ["All articles & briefings", "Full podcasts + transcripts", "10 Copilot queries/day", "Research samples", "Save & bookmark"],
    cta: "Become a Member", testid: "tier-member",
  },
  {
    name: "Premium", price_m: 499, price_y: 4990, plan_code_m: "premium_monthly", plan_code_y: "premium_yearly", featured: true,
    features: ["Everything in Member", "Unlimited Copilot", "All research reports", "Quarterly outlook downloads", "Early-access embargoed pieces", "Priority support"],
    cta: "Go Premium", testid: "tier-premium",
  },
  {
    name: "Enterprise", price_m: "Custom", price_y: null,
    features: ["Seat management & SSO (Q3)", "Custom research engagements", "Advisory access", "API for data egress", "Dedicated practice lead"],
    cta: "Talk to sales", testid: "tier-enterprise",
  },
];

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState("monthly"); // monthly | yearly
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const checkout = async (planCode) => {
    if (!user || user === false) { navigate("/login", { state: { from: "/pricing" } }); return; }
    setBusy(planCode); setErr("");
    try {
      const { data } = await api.post("/payments/checkout/plan", { plan_code: planCode, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { setErr(formatApiError(e)); setBusy(""); }
  };

  return (
    <PublicLayout>
      <section className="border-b border-[var(--ink)] bg-[var(--parchment)] py-20" data-testid="pricing-page">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="eyebrow">Membership</div>
          <h1 className="font-display font-black text-[clamp(2.6rem,6vw,5rem)] mt-4 leading-[0.95] tracking-tight">Built for the people who decide.</h1>

          {/* Toggle */}
          <div className="mt-12 inline-flex border border-[var(--ink)]">
            <button onClick={() => setCycle("monthly")} className={"px-5 py-2 font-mono text-[11px] uppercase tracking-[0.22em] " + (cycle === "monthly" ? "bg-[var(--ink)] text-white" : "text-[var(--ink)]")} data-testid="cycle-monthly">Monthly</button>
            <button onClick={() => setCycle("yearly")} className={"px-5 py-2 font-mono text-[11px] uppercase tracking-[0.22em] " + (cycle === "yearly" ? "bg-[var(--ink)] text-white" : "text-[var(--ink)]")} data-testid="cycle-yearly">Yearly · save 17%</button>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--ink)] border border-[var(--ink)]" data-testid="pricing-grid">
            {TIERS.map((t) => {
              const price = cycle === "yearly" ? (t.price_y ?? t.price_m) : t.price_m;
              const planCode = cycle === "yearly" ? t.plan_code_y : t.plan_code_m;
              const cycleLabel = typeof price === "number" ? (cycle === "yearly" ? "/yr" : "/mo") : "";
              const isFeatured = t.featured;
              return (
                <div key={t.name} className={(isFeatured ? "bg-[var(--ink)] text-[var(--parchment)]" : "bg-[var(--parchment)] text-[var(--ink)]") + " p-8 flex flex-col"} data-testid={t.testid}>
                  <div className="eyebrow" style={{ color: isFeatured ? "var(--brass)" : "var(--oxblood)" }}>{t.name}</div>
                  <div className="mt-4">
                    {typeof price === "number" ? (
                      <div className="font-display font-black text-5xl">${price}<span className="font-mono text-sm uppercase tracking-[0.18em] ml-1">{cycleLabel}</span></div>
                    ) : (
                      <div className="font-display font-black text-4xl">{price}</div>
                    )}
                  </div>
                  <ul className="mt-8 space-y-3 flex-1">
                    {t.features.map((f, i) => (
                      <li key={i} className="flex gap-3 text-sm">
                        <Check size={16} className={isFeatured ? "text-[var(--brass)] flex-shrink-0 mt-[3px]" : "text-[var(--viridian)] flex-shrink-0 mt-[3px]"} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    {t.name === "Guest" && (
                      <Link to="/register" className="btn-secondary w-full justify-center" style={isFeatured ? { color: "var(--parchment)", borderColor: "var(--parchment)" } : {}}>{t.cta}</Link>
                    )}
                    {t.name === "Enterprise" && (
                      <Link to="/enterprise" className={isFeatured ? "btn-primary w-full justify-center" : "btn-secondary w-full justify-center"}>{t.cta} <ArrowRight size={14} /></Link>
                    )}
                    {(t.name === "Member" || t.name === "Premium") && (
                      <button
                        onClick={() => checkout(planCode)}
                        disabled={busy === planCode}
                        className={isFeatured ? "w-full bg-[var(--brass)] text-[var(--ink)] py-3 font-mono text-xs uppercase tracking-[0.22em] hover:bg-[var(--signal)] transition-colors flex items-center justify-center gap-2" : "btn-primary w-full justify-center"}
                        data-testid={`checkout-${t.name.toLowerCase()}`}
                      >
                        {busy === planCode ? "Redirecting…" : <>{t.cta} <ArrowRight size={14} /></>}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {err && <p className="mt-6 text-sm font-mono text-red-700">{err}</p>}
        </div>
      </section>
    </PublicLayout>
  );
}
